import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {NodeProps} from 'reactflow';

import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {
  getExpectedStepDataKind,
  getInputStepDataShape,
  getOutputStepDataShape,
} from 'pipelines/StepDataUtils';
import {getIconForStepDataKind} from 'pipelines/StepIconUtils';
import {BenchlingNodeV2} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNode';
import removeNulls from 'util/removeNulls';

import {AnalysisFlowchartAction} from './actions/AnalysisFlowchartActionTypes';
import {AnalysisGraphNodeData} from './analysis-graph/AnalysisGraphTypes';
import {useAnalysisFlowchartContext} from './AnalysisFlowchartContext';
import StepDataHandle from './handles/StepDataHandle';
import StepMenu from './menus/StepMenu';
import getStepNodeContents from './node-contents/getStepNodeContents';
import StepPopover from './popovers/StepPopover';

type AnalysisFlowchartNodeProps = Pick<
  NodeProps<AnalysisGraphNodeData>,
  'data' | 'dragging' | 'id' | 'xPos' | 'yPos'
>;

/**
 * Check if the input or output step datas section should be hidden.
 *
 * An input or output step datas section is hidden if there is a single step data and the step only processes
 * data frames.
 */
export function hideStepDatasSection(step: PipelineStep, role: 'INPUT' | 'OUTPUT'): boolean {
  const stepDatas = role === 'INPUT' ? step.inputStepDatas : step.outputStepDatas;
  if (stepDatas.length === 0) {
    return true;
  }
  if (stepDatas.length > 1) {
    return false;
  }
  return [...step.inputStepDatas, ...step.outputStepDatas].every(
    (stepData) => getExpectedStepDataKind(step, stepData) === PipelineStepDataKind.DATASET
  );
}

export default function AnalysisFlowchartNode({
  data: {step},
  dragging,
}: AnalysisFlowchartNodeProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const {mode, getActionInfo, highlightedStepIds, selectedStepId} = useAnalysisFlowchartContext();
  const isDimmed = highlightedStepIds.size > 0 && !highlightedStepIds.has(step.id);
  const isSelected = selectedStepId === step.id;

  const {nodeName, nodeIcon, nodeStatus, nodeSecondaryInfo} = getStepNodeContents(step, mode);

  const hideInputStepDatasSection = hideStepDatasSection(step, 'INPUT');
  const hideOutputStepDatasSection = hideStepDatasSection(step, 'OUTPUT');

  const sections = removeNulls([
    hideInputStepDatasSection
      ? null
      : {
          leftHeader: 'Inputs',
          sectionRows: step.inputStepDatas.map((stepData) => ({
            leftHandle: <StepDataHandle step={step} stepData={stepData} />,
            primaryText: getInputStepDataShape(step.stepType, step.stepVersion, stepData.name).displayName,
            secondaryText: '',
            icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
          })),
        },
    hideOutputStepDatasSection
      ? null
      : {
          leftHeader: 'Outputs',
          sectionRows: step.outputStepDatas.map((stepData) => ({
            rightHandle: <StepDataHandle step={step} stepData={stepData} />,
            primaryText: getOutputStepDataShape(step.stepType, step.stepVersion, stepData.name).displayName,
            secondaryText: '',
            icon: getIconForStepDataKind(getExpectedStepDataKind(step, stepData)),
          })),
        },
  ]);

  return (
    <StepPopover step={step} disabled={isMenuOpen || dragging}>
      <StepMenu step={step} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
        <div
          className={css(styles.node, isDimmed && styles.dimmedNode)}
          onClick={() => {
            getActionInfo(AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION, step.id).doAction?.(step.id);
          }}
          onContextMenu={(e) => {
            setIsMenuOpen(true);
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <BenchlingNodeV2.Standard.Node
            headerProps={{
              title: nodeName,
              leftIcon: nodeIcon,
              subTitle: nodeSecondaryInfo,
              leftHandle:
                hideInputStepDatasSection && step.inputStepDatas.length === 1 ? (
                  <StepDataHandle step={step} stepData={step.inputStepDatas[0]} />
                ) : null,
              rightHandle:
                hideOutputStepDatasSection && step.outputStepDatas.length === 1 ? (
                  <StepDataHandle step={step} stepData={step.outputStepDatas[0]} />
                ) : null,
            }}
            isSelected={isSelected}
            status={nodeStatus}
            sectionsProps={sections.length > 0 ? {sections} : undefined}
          />
        </div>
      </StepMenu>
    </StepPopover>
  );
}

const styles = StyleSheet.create({
  node: {
    wordBreak: 'break-word', // `Float` applies `break-all`, which breaks in the middle of words
  },
  dimmedNode: {
    opacity: 0.3,
    transition: 'opacity 200ms ease-in-out',
  },
});
